/**
 * CC_07 Block 4 — Pop-Up 2 „Datei hinzufügen". One dialog to create a new file
 * (Board / Notiz / Liste) OR a folder, replacing the per-filetype buttons. The
 * SAME dialog is used in „Meine Dateien" and „Für mich freigegeben" (D-42-Zusatz
 * — Einstiegspunkt-Konsolidierung) so a member can also create inside a shared
 * folder (Block 5). The dialog only collects intent + name; the caller performs
 * the guarded create.
 *
 * CC_11-Revision (D-62/D-63) — „Aus Vorlage": when the caller passes templates,
 * a fifth choice lists them and creation goes through the fork endpoint (the
 * caller's onCreateFromTemplate) instead of a blank create.
 *
 * Welle B (Punkt 10) — der Dialog beginnt jetzt mit einem Modus-Umschalter
 * „Erstellen / Hochladen". „Erstellen" bietet zusätzlich den Typ **URL**
 * (Lesezeichen: nur ein Ziel + Name). „Hochladen" nimmt eine Datei entgegen; die
 * Format→Typ-Zuordnung macht der Aufrufer (upload-map). Beide neuen Pfade sind
 * optional — ohne die Props bleibt der Dialog wie zuvor reines Erstellen.
 */

import { useState } from "react";

import "./ryfe.scss";

import type { TemplateFile } from "../api";

export type NewEntryKind = "board" | "md" | "list" | "folder";

const CHOICES: { kind: NewEntryKind; label: string; icon: string }[] = [
  { kind: "board", label: "Board", icon: "🧩" },
  { kind: "md", label: "Notiz", icon: "📝" },
  { kind: "list", label: "Liste", icon: "🗂" },
  { kind: "folder", label: "Ordner", icon: "📁" },
];

const DEFAULT_NAME: Record<NewEntryKind, string> = {
  board: "Neues Board",
  md: "Neue Notiz",
  list: "Neue Liste",
  folder: "Neuer Ordner",
};

// „url" ist im Erstellen-Modus eine eigene Auswahl neben den NewEntryKind-Typen,
// wird aber nicht über onCreate geführt (es braucht zusätzlich das Ziel).
type CreateChoice = NewEntryKind | "template" | "url";

export const AddFileDialog = ({
  onClose,
  onCreate,
  templates,
  onCreateFromTemplate,
  onCreateUrl,
  onUpload,
  onUploadFolder,
  busy = false,
}: {
  onClose: () => void;
  onCreate: (kind: NewEntryKind, name: string) => void | Promise<void>;
  /** CC_11 (D-62/D-63) — the workspace's templates; absent = no template tab. */
  templates?: TemplateFile[];
  onCreateFromTemplate?: (
    templateId: string,
    name: string,
  ) => void | Promise<void>;
  /** Welle B (Punkt 10) — URL-Datei anlegen (Lesezeichen). Absent = kein URL-Typ. */
  onCreateUrl?: (name: string, url: string) => void | Promise<void>;
  /** Welle B (Punkt 10) — Datei hochladen. Absent = kein Hochladen-Modus. */
  onUpload?: (file: File) => void | Promise<void>;
  /** AP-7 (P17.2) — rekursiver Ordner-Upload. Absent = kein Ordner-Upload. */
  onUploadFolder?: (files: File[]) => void | Promise<void>;
  busy?: boolean;
}) => {
  const [mode, setMode] = useState<"create" | "upload">("create");
  const [kind, setKind] = useState<CreateChoice>("board");
  const [name, setName] = useState("");
  const [templateId, setTemplateId] = useState<string>("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);

  const showTemplates = !!onCreateFromTemplate && (templates?.length ?? 0) > 0;
  const activeTemplate =
    kind === "template"
      ? (templates || []).find((t) => t.id === templateId) || null
      : null;
  const placeholder =
    kind === "template"
      ? activeTemplate
        ? `${activeTemplate.name} (Kopie)`
        : "Name"
      : kind === "url"
      ? "Neues Lesezeichen"
      : DEFAULT_NAME[kind];

  const submit = async () => {
    if (busy) {
      return;
    }
    if (kind === "template") {
      if (!templateId || !onCreateFromTemplate) {
        return;
      }
      await onCreateFromTemplate(templateId, name.trim() || placeholder);
      return;
    }
    if (kind === "url") {
      const u = url.trim();
      if (!/^https?:\/\//i.test(u)) {
        setError("Bitte eine http(s)-URL angeben.");
        return;
      }
      await onCreateUrl?.(name.trim() || u, u);
      return;
    }
    await onCreate(kind, name.trim() || DEFAULT_NAME[kind]);
  };

  return (
    <div
      className="ryfe-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div className="ryfe-modal__card" onClick={(e) => e.stopPropagation()}>
        <h2 className="ryfe-panel__title">Datei hinzufügen</h2>

        {/* Welle B (Punkt 10) — Modus-Umschalter Erstellen/Hochladen. Nur wenn
            der Aufrufer Hochladen unterstützt (onUpload gesetzt). */}
        {onUpload && (
          <div className="ryfe-elmenu__seg ryfe-addfile__mode">
            <button
              type="button"
              data-testid="addfile-mode-create"
              className={
                mode === "create"
                  ? "ryfe-elmenu__size is-active"
                  : "ryfe-elmenu__size"
              }
              onClick={() => setMode("create")}
            >
              ＋ Erstellen
            </button>
            <button
              type="button"
              data-testid="addfile-mode-upload"
              className={
                mode === "upload"
                  ? "ryfe-elmenu__size is-active"
                  : "ryfe-elmenu__size"
              }
              onClick={() => setMode("upload")}
            >
              ⬆ Hochladen
            </button>
          </div>
        )}

        {mode === "upload" && onUpload ? (
          <label className="ryfe-field-label">
            Datei auswählen
            <input
              className="ryfe-field-input"
              type="file"
              data-testid="addfile-upload-input"
              disabled={busy}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = "";
                if (f) {
                  void onUpload(f);
                }
              }}
            />
            <span className="ryfe-meta__note">
              Text (.md/.txt) → Notiz · Bilder → Bild · .excalidraw → Board ·
              Tabellen &amp; sonstige (.csv/.xlsx/.pdf/.docx/…) → Datei
              (Download).
            </span>
            {/* AP-7 (P17.2) — rekursiver Ordner-Upload: Struktur + alle Dateien. */}
            {onUploadFolder && (
              <>
                <span
                  className="ryfe-field-label"
                  style={{ marginTop: "0.6rem" }}
                >
                  … oder einen ganzen Ordner
                </span>
                <input
                  className="ryfe-field-input"
                  type="file"
                  data-testid="addfile-upload-folder"
                  disabled={busy}
                  // webkitdirectory: der Ordner-Upload liefert die Dateien mit
                  // webkitRelativePath; die Struktur wird nachgebaut.
                  {...({
                    webkitdirectory: "",
                    directory: "",
                  } as Record<string, string>)}
                  onChange={(e) => {
                    const list = e.target.files
                      ? Array.from(e.target.files)
                      : [];
                    e.target.value = "";
                    if (list.length) {
                      void onUploadFolder(list);
                    }
                  }}
                />
              </>
            )}
          </label>
        ) : (
          <>
            <div className="ryfe-elmenu__seg ryfe-addfile__types">
              {CHOICES.map((c) => (
                <button
                  key={c.kind}
                  type="button"
                  className={
                    kind === c.kind
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setKind(c.kind)}
                >
                  {c.icon} {c.label}
                </button>
              ))}
              {onCreateUrl && (
                <button
                  type="button"
                  data-testid="addfile-url"
                  className={
                    kind === "url"
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setKind("url")}
                >
                  🔗 URL
                </button>
              )}
              {showTemplates && (
                <button
                  type="button"
                  data-testid="addfile-template"
                  className={
                    kind === "template"
                      ? "ryfe-elmenu__size is-active"
                      : "ryfe-elmenu__size"
                  }
                  onClick={() => setKind("template")}
                >
                  📋 Aus Vorlage
                </button>
              )}
            </div>

            {kind === "template" && (
              <label className="ryfe-field-label">
                Vorlage
                <select
                  className="ryfe-select"
                  data-testid="addfile-template-pick"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                >
                  <option value="">— Vorlage wählen —</option>
                  {(templates || []).map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.kind})
                    </option>
                  ))}
                </select>
              </label>
            )}

            {kind === "url" && (
              <label className="ryfe-field-label">
                Ziel-URL
                <input
                  className="ryfe-field-input"
                  data-testid="addfile-url-input"
                  value={url}
                  placeholder="https://…"
                  onChange={(e) => setUrl(e.target.value)}
                />
              </label>
            )}

            <label className="ryfe-field-label">
              Name
              <input
                className="ryfe-field"
                autoFocus
                value={name}
                placeholder={placeholder}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !busy) {
                    void submit();
                  }
                }}
              />
            </label>

            {error && (
              <div className="ryfe-auth__error" role="alert">
                {error}
              </div>
            )}

            <div className="ryfe-modal__actions">
              <span className="ryfe-topbar__spacer" />
              <button className="ryfe-btn ryfe-btn--ghost" onClick={onClose}>
                Abbrechen
              </button>
              <button
                className="ryfe-btn ryfe-btn--primary"
                disabled={busy || (kind === "template" && !templateId)}
                onClick={() => void submit()}
              >
                Anlegen
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
